/* eslint-disable parog-web/no-glint-expect-error-directive */
import type Store from '@ember-data/store';
import { service } from '@ember/service';
import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import { validatePresence } from 'ember-changeset-validations/validators';
import type { BufferedChangeset, ValidatorMap } from 'ember-changeset/types';
import type FlashMessagesService from 'ember-cli-flash/services/flash-messages';
import type { IntlService } from 'ember-intl';
import Question from 'parog-web/components/compound-survey/edition/question';
import type { ForcBaseArgs } from 'parog-web/components/forc/abstract/base-component';
import ForcBaseComponent from 'parog-web/components/forc/abstract/base-component';
import type { ToastMessage } from 'parog-web/components/forc/abstract/journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import capitalize from 'parog-web/helpers/capitalize';
import type { SerializedSurvey } from 'parog-web/models/compound-survey';
import CompoundSurveyCompletedJournalEvent from 'parog-web/models/compound-survey-completed-journal-event';
import type NavigationService from 'parog-web/services/navigation';
import type SemeiaSessionService from 'parog-web/services/session';
import DatetimePicker from '../changeset/datetime-picker';

interface Args extends ForcBaseArgs {
  surveyCompletedJe: CompoundSurveyCompletedJournalEvent;
}

export default class CompoundSurveyEditionComponent extends ForcBaseComponent<Args> {
  static fullComponentId = 'constant-monitoring/edition-form';

  @service declare flashMessages: FlashMessagesService;
  @service declare intl: IntlService;
  @service declare navigation: NavigationService;
  @service declare session: SemeiaSessionService;
  @service declare store: Store;

  override afterCancel = this.transitionToDashboard;
  override afterCompleted = this.transitionToDashboard;

  /**
   * The changeset encapsulate the answers object map
   */
  get changeset(): BufferedChangeset {
    if (this._changeset) return this._changeset;
    this._changeset = Changeset(
      this.args.surveyCompletedJe,
      lookupValidator(this.validations),
      this.validations
    );
    return this._changeset;
  }

  override get isDirty() {
    return this.changeset.isDirty;
  }

  get questions(): SerializedSurvey {
    return this.args.surveyCompletedJe.surveyRaw;
  }

  get toastMessage(): ToastMessage {
    return this.session.patient
      ? {
          message: 'Questionnaire enregistré. Merci !',
          title: 'Questionnaire'
        }
      : {
          message: 'Information bien enregistrée. Merci !'
        };
  }

  get validations(): ValidatorMap {
    const answersValidations = {};
    this.questions.forEach(q => {
      answersValidations[q.id] = validatePresence({
        presence: true,
        description: 'La question'
      });
    });
    const validations = CompoundSurveyCompletedJournalEvent.validations(
      this.intl
    );
    validations['answers'] = answersValidations;
    return validations;
  }

  private declare _changeset: BufferedChangeset;

  override async isValid(): Promise<boolean> {
    await this.changeset.validate();
    return this.changeset.get('isValid');
  }

  override async save(): Promise<void> {
    await this.changeset.validate();
    this.changeset.execute();
    this.args.surveyCompletedJe.patient ||= this.patient;
    await this.args.surveyCompletedJe.save();
    this.flashMessages.success(this.toastMessage.message, {
      title: this.toastMessage.title
    });
  }

  private transitionToDashboard(): void {
    this.router.transitionTo(
      this.navigation.currentPatientRouteName + '.dashboard'
    );
  }

  <template>
    <Provider
      {{! @glint-expect-error }}
      @forcComponent={{this}}
      @title={{capitalize @surveyCompletedJe.survey.title}}
    >
      <:content>
        <div class='field'>
          <DatetimePicker
            @changeset={{this.changeset}}
            @propertyName='occurredAt'
            @labelName='Date à laquelle le questionnaire a été renseigné'
          />
        </div>
        {{#each this.questions as |question|}}
          {{! @glint-expect-error }}
          <Question @question={{question}} @changeset={{this.changeset}} />
        {{/each}}
      </:content>
    </Provider>
  </template>
}
